import { useContext } from "react";
import useBuildDeal from "./useBuildDeal.ts";
import useDealBuilderInputsStore from "../../stores/deal-builder-inputs.store.ts";
import useDealBuilderExtractionStore from "../../stores/deal-builder-extraction.store.ts";
import { mapPrimaryErrorsToPrefill } from "../utils/primary-errors-import.utils.ts";
import { MainTabContext, TAB_NAMES } from "../../contexts/MainTabContext.js";
import Result from "../utils/result.utils.ts";
import type { PrimaryErrorEntry } from "../../analysis/hooks/useAnalysis.ts";

/**
 * Loads every bond of one issue from the Primary Errors table into the deal
 * builder and builds it, then moves the user over to that tab.
 *
 * Nothing is passed through navigation: the stores are module level, so
 * writing to them and switching tab is enough, the same way the pricing
 * management deals page opens a saved deal.
 *
 * An issue we can't map is reported back to the caller rather than through the
 * deal builder snackbar, because that snackbar lives inside DealBuilderMain
 * and isn't mounted while the user is still looking at the Analysis tab.
 */
export default function useLoadIssueIntoDealBuilder() {
    const { buildDeal } = useBuildDeal();
    const { setExtraction } = useDealBuilderExtractionStore();
    const { setActiveTab } = useContext(MainTabContext);

    async function loadIssue(entries: PrimaryErrorEntry[]): Promise<Result<undefined>> {
        const current = useDealBuilderInputsStore.getState();
        const prefill = mapPrimaryErrorsToPrefill(entries, current);

        if (!prefill.valid) return { valid: false, error: prefill.error };

        current.updateAll({ ...current, ...prefill.body.inputPatch });

        // No review fields or warnings: this data is deterministic, so there is
        // nothing for the user to double check.
        setExtraction(
            prefill.body.bonds,
            prefill.body.termRanges,
            [],
            [],
            `Issue ${entries[0].issue_id}`,
        );

        setActiveTab(TAB_NAMES.DEAL_BUILDER);
        await buildDeal();

        return { valid: true };
    }

    return { loadIssue };
}
